import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadControlCentre, savePlanningChange, projectCardTitle, planningResourceId, type ControlSnapshot, type CapacityDay, type PlanningChange, type DeliveryRisk,
} from '../services/projectControlCentreService';
import PlanningEditor, { type EditorTarget, HoursField, statusLabels } from './ProjectControlPlanningEditor';
import ProjectControlWip from './ProjectControlWip';
import ProjectTurnaround, { type TimingEditor } from './ProjectTurnaround';
import TodayMailReview from '../components/projects/TodayMailReview';
import './ProjectControlCentrePage.css';

type View = 'Today' | 'Capacity' | 'Risk & decisions';
const views: View[] = ['Today', 'Capacity', 'Risk & decisions'];
const severity: Record<DeliveryRisk['risk'], number> = { critical: 0, high: 1, unknown: 2, medium: 3, low: 4 };
const hours = (value: number | null) => value == null ? 'Unknown' : `${Number(value.toFixed(2))}h`;
function dateLabel(date: string | null) {
  if (!date) return 'Unknown';
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(date + 'T00:00:00Z'));
}
function weekDates(date: string, today: string, through: string) {
  const cursor = new Date(date + 'T00:00:00Z');
  cursor.setUTCDate(cursor.getUTCDate() - (cursor.getUTCDay() + 6) % 7);
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    const key = cursor.toISOString().slice(0, 10);
    if (key >= today && key <= through) days.push(key);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}
function shiftDay(date: string, days: number) {
  const next = new Date(date + 'T00:00:00Z');
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

function capacityTotals(rows: CapacityDay[], expectedDays: number) {
  return {
    available: rows.length === expectedDays && rows.every(row => row.availableHours !== null)
      ? rows.reduce((sum, row) => sum + row.availableHours!, 0) : null,
    booked: rows.reduce((sum, row) => sum + row.allocatedHours, 0),
    free: rows.length === expectedDays && rows.every(row => row.freeHours !== null)
      ? rows.reduce((sum, row) => sum + row.freeHours!, 0) : null,
  };
}

function PersonHours({ period, totals, scale }: { period: string; totals: ReturnType<typeof capacityTotals>; scale: number }) {
  const width = (n: number) => (Math.min(1, n / Math.max(scale, 1)) * 100) + '%';
  return <div className="pcc-person-hours">
    <div><b>{period}</b><span>{hours(totals.available)} available</span></div>
    <div className="pcc-hours-track" aria-hidden="true">
      <i style={{ width: width(totals.booked) }} />
      {totals.free !== null && <i className="free" style={{ width: width(totals.free) }} />}
    </div>
    <small>Allocated {hours(totals.booked)} · Free {hours(totals.free)}</small>
  </div>;
}
function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'The control centre could not be reached.';
}

export default function ProjectControlCentrePage() {
  const [data, setData] = useState<ControlSnapshot | null>(null);
  const [view, setView] = useState<View>('Today');
  const [target, setTarget] = useState<EditorTarget | null>(null);
  const [timingEditor, setTimingEditor] = useState<TimingEditor | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [day, setDay] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [wipResource, setWipResource] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const requestId = useRef(0);
  const refresh = useCallback(async (background = false) => {
    const id = ++requestId.current;
    if (!background) { setLoading(true); setError(''); }
    try {
      const snapshot = await loadControlCentre();
      if (id === requestId.current) { setData(snapshot); setError(''); }
    }
    catch (e) { if (id === requestId.current) { setError(errorMessage(e)); if (!background) setData(null); } }
    finally { if (id === requestId.current) setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); return () => { requestId.current++; }; }, [refresh]);
  useEffect(() => {
    if (view !== 'Today' || !data?.turnaroundAvailable || busy || target || timingEditor || loading) return;
    // The API evaluates the configured business date. Keep open Today tabs current across midnight.
    const check = () => { if (document.visibilityState === 'visible') void refresh(true); };
    const timer = window.setInterval(check, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', check); };
  }, [view, data?.turnaroundAvailable, busy, target, timingEditor, loading, refresh]);
  async function save(change: PlanningChange) {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await savePlanningChange(change);
      setTarget(null);
      setTimingEditor(null);
      setNotice('Changes saved.');
      await refresh();
    } catch (e) {
      setError(errorMessage(e) + ((e as { status?: number })?.status === 409 ? ' Refresh the plan and reopen this record before saving.' : ''));
    } finally { setBusy(false); }
  }
  function edit(next: EditorTarget) { setNotice(''); setError(''); setTimingEditor(null); setTarget(next); }
  function editTiming(next: TimingEditor | null) { setNotice(''); setError(''); setTarget(null); setTimingEditor(next); }
  const date = data && day >= data.today && day <= data.through ? day : data?.today || '';
  const projects = new Map(data?.projects.map(p => [p.projectId, p]));
  const packages = new Map(data?.packages.map(p => [p.id, p]));
  const delivered = new Set(data?.timingProfiles?.filter(p => p.deliveredDate).map(p => p.projectId));
  const risks = (data?.plan.projects || []).filter(p => !delivered.has(p.projectId)).sort((a, b) => severity[a.risk] - severity[b.risk]);
  const atRisk = risks.filter(p => p.risk === 'critical' || p.risk === 'high').length;
  const todays = data?.plan.days.filter(row => row.date === data.today) || [];
  const confirmed = todays.filter(row => row.freeHours != null);
  const available = confirmed.length ? hours(confirmed.reduce((sum, r) => sum + (r.freeHours ?? 0), 0)) : 'Unknown';
  const projectsWithPackages = new Set(data?.packages.map(pkg => pkg.projectId));
  const unplanned = data?.projects.filter(project => !projectsWithPackages.has(project.projectId)).length || 0;
  const work = data?.packages.filter(p => !projectFilter || p.projectId === projectFilter) || [];
  const inlineItem = target?.kind === 'package' && target.focusHours ? target.item : undefined;
  const dailyRows = data?.plan.days.filter(row => row.date === date) || [];

  const displayedWeek = data && view === 'Capacity' ? weekDates(date, data.today, data.through) : [];
  const weekRows = new Map<string, CapacityDay[]>();
  for (const row of data?.plan.days ?? []) {
    if (!displayedWeek.includes(row.date)) continue;
    const group = weekRows.get(row.resourceId) ?? [];
    group.push(row);
    weekRows.set(row.resourceId, group);
  }
  const weekTotals = new Map([...weekRows].map(([id, rows]) => [id, capacityTotals(rows, displayedWeek.length)]));
  const weekScale = Math.max(1, ...[...weekTotals.values()].map(t => Math.max(t.available ?? 0, t.booked)));
  const dayScale = Math.max(1, ...dailyRows.map(r => Math.max(r.availableHours ?? 0, r.allocatedHours)));
  return <div className={view === 'Today' ? 'pcc pcc-today' : 'pcc'}>
    <header className="pcc-mast"><a href="/dashboard" aria-label="Dashboard" className="pcc-monogram">PM</a>
      <h1>Project Control Centre</h1>
      {data && <div className="pcc-bar"><nav aria-label="Control centre views">
        {views.map(v => <button key={v} disabled={busy} aria-current={view === v ? 'page' : undefined}
          onClick={() => { setView(v); setTarget(null); setTimingEditor(null); setError(''); setNotice(''); }}>{v}</button>)}</nav>
        <span className="pcc-asof">{dateLabel(data.today)}</span></div>}
      <button disabled={busy || loading} onClick={() => { setTarget(null); setTimingEditor(null); setNotice(''); void refresh(); }}>Refresh plan</button>
    </header>
    {error && <div className="pcc-alert" role="alert">{error}</div>}
    {notice && <div className="pcc-notice" role="status">{notice}</div>}
    {loading && <p role="status">Loading the current plan…</p>}
    {data && <>
      <div className="pcc-stats" aria-label="Portfolio summary">
        <div><span>Projects with work</span><strong>{new Set(data.packages.filter(p => p.status !== 'complete').map(p => p.projectId)).size}</strong></div>
        <div><span>High or critical risk</span><strong>{atRisk}</strong></div>
        <div><span>Free capacity today</span><strong>{available}</strong></div>
        <div><span>In-progress packages</span><strong>{data.packages.filter(p => p.status === 'in_progress').length}</strong></div>
      </div>
      {unplanned > 0 && <p className="pcc-coverage"><strong>Planning coverage</strong> · {unplanned} active {unplanned === 1 ? 'project has' : 'projects have'} no recorded work packages. Their effort is missing from free-hour estimates.</p>}
      {target && !inlineItem && <PlanningEditor key={target.kind + (target.kind === 'package' ? target.item?.id : target.kind === 'capacity' ? [target.item?.resourceId ?? target.basis?.resourceId, target.item?.date ?? target.basis?.date ?? target.date].join(':') : target.kind === 'wip_limit' ? target.resourceId : target.projectId)}
        snapshot={data} target={target} busy={busy} onSave={save} onClose={() => { setTarget(null); setError(''); }} />}
      {view === 'Today' && <>
        <TodayMailReview plan={data}/>
        <ProjectTurnaround data={data} editor={timingEditor} edit={editTiming} busy={busy || loading || !!target} save={save}
          showPackages={id => { setTimingEditor(null); setProjectFilter(id); document.getElementById('pcc-work-packages')?.focus(); }} />
        <div className="pcc-command-grid"><ProjectControlWip data={data} selected={wipResource} onSelect={setWipResource}
          busy={busy || loading || !!target || !!timingEditor} edit={edit} save={save} />
        <section className="pcc-panel"><div className="pcc-section-heading"><div>
          <h2>Today’s allocations</h2></div></div>
          <p>Top three team allocations by forecast priority.</p>
          {!data.plan.priorities.length && <p>No work is allocated today. Check remaining hours, dependencies and confirmed capacity.</p>}
          {data.plan.priorities.map((id, i) => {
            const p = packages.get(id);
            if (!p) return null;
            const booked = data.plan.allocations.filter(a => a.packageId === id && a.date === data.today).reduce((sum, a) => sum + a.hours, 0);
            const project = projects.get(p.projectId);
            const resourceId = planningResourceId(data, p.resourceId);
            const decision = data.wip?.find(person => person.resourceId === resourceId)?.candidates.find(row => row.projectId === p.projectId);
            const dailyHours = todays.find(row => row.resourceId === resourceId)?.availableHours;
            return <div className="pcc-line" key={id}><div className="pcc-ranked"><b>{String(i + 1).padStart(2, '0')}</b>
              <div><strong>{p.title}</strong><small>{project?.name} · {p.resourceId} · {hours(booked)} allocated today</small>
                {p.status === 'ready' && <small>{data.wipLimitsAvailable !== false && decision?.eligible && decision.packageIds.includes(id)
                  ? 'Ready · Start requires your action' : 'On hold · Review personal start conditions'}</small>}
                {project?.committedDate && <small>Committed for {dateLabel(project.committedDate)}</small>}
                {dailyHours != null && dailyHours > 0 && <progress className="pcc-booked" max={dailyHours} value={booked} aria-label={p.title + ' share of confirmed daily capacity'} />}</div></div>
              {data.canEdit && <button disabled={busy} onClick={() => edit({ kind: 'package', item: p })}>Edit package</button>}</div>;
          })}
        </section></div>
        <section className="pcc-panel pcc-ledger"><div className="pcc-section-heading"><div>
          <h2 id="pcc-work-packages" tabIndex={-1}>Work packages</h2></div>{data.canEdit && <button className="pcc-primary" disabled={busy || !data.projects.length} onClick={() => edit({ kind: 'package', projectId: projectFilter })}>Add work package</button>}</div>
          <label className="pcc-filter">Project<select value={projectFilter} onChange={e => { setProjectFilter(e.target.value); if (inlineItem) setTarget(null); }}>
            <option value="">All projects</option>{data.projects.map(p => <option key={p.projectId} value={p.projectId}>{p.name}</option>)}</select></label>
          {!work.length && <p>{data.projects.length ? 'Add a work package to estimate remaining effort and assign an owner.' : 'No projects are available for planning.'}</p>}
          <div className="pcc-table-scroll"><table><thead><tr>{['Package','Project','Owner','Status','Remaining','Action'].map(label => <th key={label}>{label}</th>)}</tr></thead>
          <tbody>{work.map(p => <React.Fragment key={p.id}><tr className={inlineItem?.id === p.id ? 'selected' : undefined}>
            <td><strong>{p.title}</strong>{!!p.dependencies.length && <small>Depends on {p.dependencies.map(id => packages.get(id)?.title || 'Unknown package').join(', ')}</small>}</td>
            <td>{projects.get(p.projectId)?.name}</td><td>{p.resourceId || 'Unassigned'}</td>
            <td><span className={'pcc-state ' + p.status}>{statusLabels[p.status]}</span></td>
            <td>{data.canEdit && p.status !== 'complete'
              ? <button className="pcc-hours-link" disabled={busy} aria-label={'Adjust remaining hours for ' + p.title}
                  onClick={() => edit({ kind: 'package', item: p, focusHours: true })}>{hours(p.remainingHours)}</button>
              : hours(p.remainingHours)}</td>
            <td>{data.canEdit && <button disabled={busy} aria-label={'Edit ' + p.title} onClick={() => edit({ kind: 'package', item: p })}>Edit</button>}</td></tr>
            {inlineItem?.id === p.id && <tr><td colSpan={6} className="pcc-inline-cell">
              <form className="pcc-inline-hours" aria-label={'Adjust hours for ' + p.title} onSubmit={event => {
                event.preventDefault();
                const value = new FormData(event.currentTarget).get('remaining');
                void save({ ...inlineItem, kind: 'package', remainingHours: value === '' ? null : Number(value) });
              }}>
                <HoursField label="Remaining hours" name="remaining" initial={inlineItem.remainingHours} max={10000} presets={[0,1,2,4,8]} disabled={busy} />
                <button className="pcc-primary" disabled={busy} type="submit">Save changes</button>
                <button type="button" disabled={busy} onClick={() => setTarget(null)}>Cancel</button>
              </form></td></tr>}
          </React.Fragment>)}</tbody></table></div></section>
      </>}
      {view === 'Capacity' && <section className="pcc-panel"><div className="pcc-section-heading"><div><span className="pcc-kicker">Resource calendar</span>
        <h2>Daily capacity</h2></div>{data.canEdit && <button className="pcc-primary" disabled={busy} onClick={() => edit({ kind: 'capacity', date })}>Record capacity</button>}</div>
        {dailyRows.some(row => row.source) && <p className="pcc-calendar-policy">
          Staff default to 8h Monday–Friday. Wilson has 6h on Monday, Wednesday and Friday. Steven requires dated confirmation.
          A dated entry overrides the weekly pattern for that day.
        </p>}
        <div className="pcc-week-controls">
          <button disabled={busy || displayedWeek[0] === data.today} onClick={() => setDay(shiftDay(date, -7) < data.today ? data.today : shiftDay(date, -7))}>Previous week</button>
          <strong>{dateLabel(displayedWeek[0])} – {dateLabel(displayedWeek[displayedWeek.length - 1])}</strong>
          <button disabled={busy || displayedWeek[displayedWeek.length - 1] === data.through} onClick={() => setDay(shiftDay(date, 7) > data.through ? data.through : shiftDay(date, 7))}>Next week</button>
          <button disabled={busy || date === data.today} onClick={() => setDay(data.today)}>Back to today</button>
        </div>
        <label className="pcc-filter">Work date<input type="date" min={data.today} max={data.through} value={date}
          onChange={e => { if (e.target.value >= data.today && e.target.value <= data.through) setDay(e.target.value); }} /></label>
        <div className="pcc-week" aria-label="Free planning hours by date">
          {displayedWeek.map(key => {
            const rows = data.plan.days.filter(r => r.date === key && r.freeHours !== null);
            const free = rows.length ? hours(rows.reduce((sum, r) => sum + r.freeHours!, 0)) : 'Unknown';
            return <button key={key} aria-pressed={key === date} aria-label={dateLabel(key) + ', free capacity ' + free}
              onClick={() => setDay(key)}><span>{new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', timeZone: 'UTC' })
                .format(new Date(key + 'T00:00:00Z'))}</span><strong>{free}</strong></button>;
          })}
        </div>
        {!dailyRows.length && <p>No resource calendar is available for this date. Record confirmed productive hours to allocate work.</p>}
        {!!dailyRows.length && <div className="pcc-hours-legend"><span>Allocated</span><span>Free</span></div>}
        {dailyRows.map(row => {
          const entries = data.capacity.filter(c => planningResourceId(data, c.resourceId) === row.resourceId && c.date === date);
          const source = entries.length > 1 ? 'Multiple date-specific entries' : entries.length ? 'Date-specific entry' : row.source === 'external' ? 'External · no dated confirmation'
            : row.source === 'weekly' ? (row.availableHours === 0 ? 'Weekly default · day off' : 'Weekly default · scheduled workday')
            : 'No capacity recorded';
          return <div className="pcc-resource-row" key={row.resourceId}><div><strong>{row.resourceId}</strong>
            <small>{source}</small>
            {entries.map(c => <small key={c.resourceId}>{entries.length > 1 ? c.resourceId + ' · ' : ''}{c.confirmed ? 'Confirmed' : `Unconfirmed · ${hours(c.hours)} offered`}</small>)}
            </div>
            <PersonHours period="Selected day" totals={capacityTotals([row], 1)} scale={dayScale} />
            <PersonHours period="Displayed week" totals={weekTotals.get(row.resourceId)!} scale={weekScale} />
            <div className="pcc-resource-actions">
            {data.canEdit && (entries.length ? entries.map(c => <button key={c.resourceId} disabled={busy} aria-label={`Edit capacity for ${c.resourceId}`} onClick={() => edit({ kind: 'capacity', item: c })}>
              {entries.length > 1 ? 'Edit ' + c.resourceId : 'Edit'}</button>)
              : row.source && <button disabled={busy} aria-label={`Set date override for ${row.resourceId}`} onClick={() => edit({ kind: 'capacity', basis: row })}>Set date override</button>)}</div></div>;
        })}
      </section>}
      {view === 'Risk & decisions' && <section className="pcc-panel"><div className="pcc-section-heading"><div><span className="pcc-kicker">Delivery oversight</span>
        <h2>Project delivery risk</h2></div></div>
        <p>Delivery slack shows the spare hours at the tightest handoff. A negative value indicates a shortfall against the commitment.</p>
        <label className="pcc-filter">Risk<select value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
          <option value="">All risk levels</option>{Object.keys(severity).map(s => <option key={s} value={s}>{s}</option>)}</select></label>
        {!risks.some(r => !riskFilter || r.risk === riskFilter) && <p>No projects match this view.</p>}
        <div className="pcc-risk-cards">{risks.filter(r => !riskFilter || r.risk === riskFilter).map(r => {
          const project = projects.get(r.projectId);
          const title = projectCardTitle(project);
          const projectWork = data.packages.filter(p => p.projectId === r.projectId && p.status !== 'complete');
          return <article className="pcc-risk pcc-project-risk" key={r.projectId} aria-labelledby={'risk-project-' + r.projectId}>
          <header><span className="pcc-project-ref">{project?.projectNumber || 'Project ID ' + r.projectId}</span>
            <span className={'pcc-badge ' + r.risk}>{r.risk}</span></header>
          <h3 id={'risk-project-' + r.projectId}>{title}</h3>
          {project?.siteAddress && project.siteAddress !== title && <small>{project.siteAddress}</small>}
          {projectWork.length > 0 && <small>Open work · {projectWork.map(p => p.title).join(', ')}</small>}
          <dl><div><dt>Client commitment</dt><dd>{r.committedDate ? dateLabel(r.committedDate) : 'Not recorded'}</dd></div>
            <div><dt>Forecast finish</dt><dd>{dateLabel(r.forecastDate)}</dd></div><div><dt>Delivery slack</dt><dd>{hours(r.slackHours ?? null)}</dd></div><div><dt>Deadline shortfall</dt><dd>{hours(r.shortfallHours)}</dd></div></dl>
          {!!r.reasons.length && <ul>{r.reasons.map((reason, i) => <li key={i}>{reason}</li>)}</ul>}
          {data.canEdit && <button disabled={busy} onClick={() => edit({ kind: 'commitment', projectId: r.projectId })}>Edit commitment</button>}
        </article>; })}</div></section>}
    </>}
  </div>;
}
